import React from 'react';
import { observer } from 'mobx-react-lite';
import { AdditionalInfosStoreProvider } from '../hooks/use-additional-infos/additional-infos-provider';
import { ShowErrorAlertProvider } from '../hooks/use-show-error-alert/use-show-error-alert';
import { useAdditionalInfosProviderUtils } from '../../common/hooks/use-additional-infos-provider-utils';
import { useCrossClassEventsIds } from '../../common/hooks/use-cross-class-events-ids';
import { useEventMetadataStore } from '../../event-panel/event-panel-form/event-metadata';
import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';
import { SortDirection } from '../hooks/use-additional-infos/types';

/**
 * Reads event metadata, user info, and cross-class IDs from context,
 * then passes them as explicit props to AdditionalInfosStoreProvider.
 *
 * Shared wrapper used by EventPanel, FR Assigned Event page, and FR Narrative tab.
 */
export const EventAdditionalInfosWrapper = observer(function EventAdditionalInfosWrapper({
    children,
}: {
    children: React.ReactNode;
}): React.ReactElement {
    const metadata = useEventMetadataStore()?.state?.metadata;
    const crossClassEventsIds = useCrossClassEventsIds();
    const {
        showErrorAlert,
        userId,
        notificationManager,
        initialFilters,
    } = useAdditionalInfosProviderUtils();
    const { defaultNarrativeDirectionNewOnTop } = useEnabledFeatures();
    const initialSortDirection =
        defaultNarrativeDirectionNewOnTop === false ? SortDirection.ASC : SortDirection.DESC;

    if (!metadata?.eventId || !metadata.agencyEventNumber) {
        // eslint-disable-next-line react/jsx-no-useless-fragment
        return <>{children}</>;
    }

    return (
        <ShowErrorAlertProvider showErrorAlert={showErrorAlert}>
            <AdditionalInfosStoreProvider
                entityId={metadata.eventId}
                entityNumber={metadata.agencyEventNumber}
                currentUserId={userId}
                eventOptions={{ isScheduledEvent: metadata.isScheduledEvent }}
                notificationManager={notificationManager}
                initialFilter={initialFilters}
                initialSortDirection={initialSortDirection}
                crossClassEventsIds={crossClassEventsIds}
                enablePinnedEntries
            >
                {children}
            </AdditionalInfosStoreProvider>
        </ShowErrorAlertProvider>
    );
});
